import type { GpuRenderDiagnostics, MethodParams, Region, StampShape } from '@amitone/dither-core';
import { createMissingPatternImage } from './util/placeholder-assets';
import type { RegionUndoSnapshot } from './util/region-undo';
import type { ToneMethodField } from './util/tone-method-fields';

export type { RegionUndoSnapshot };

export type RegionEntry = {
  uuid: string;
  label: number;
  rgb: [number, number, number];
  userName: string;
  override: MethodSnapshot | null;
  /** UUID of the containing RegionStack, or null when ungrouped. */
  stackId: string | null;
};

export type RegionStack = {
  uuid: string;
  /** Display name shown in the stack list UI (e.g. "Stack 1"). */
  userName: string;
  override: MethodSnapshot | null;
};

export type RegionTemplateGroup = {
  templateRegionId: string;
  rgb: [number, number, number];
  preset: MethodSnapshot | null;
  stackId: string | null;
};

export type RegionTemplateBinding = {
  templateRegionId: string;
  rgb: [number, number, number];
  stackId: string | null;
  boundRegionUuid: string | null;
  boundLabel: number | null;
  candidateRegionUuids?: string[];
};

export type RegionTemplateStack = {
  templateStackId: string;
  userName: string;
  preset: MethodSnapshot | null;
};

export type TemplateStackState = {
  activeTemplateId: string | null;
  name: string | null;
  groups: RegionTemplateGroup[];
  stacks: RegionTemplateStack[];
  bindings: RegionTemplateBinding[];
};

export type RegionOverlayStyle = {
  /** Fill alpha byte for the active-region overlay. Range 0..255. Default is about 50%. */
  fillAlpha: number;
  /** Boundary width in source pixels. Range 1..8. */
  borderWidthPx: number;
};

export type ReferenceOverlayState = {
  image: ImageData | null;
  opacity: number;
  enabled: boolean;
};

export const REGION_GRID_COLUMNS_STORAGE_KEY = 'amitone-region-grid-columns';
export const GPU_ENABLED_STORAGE_KEY = 'amitone-gpu-enabled';

export function normaliseRegionGridColumns(value: number): number {
  if (!Number.isFinite(value)) return 3;
  return Math.min(5, Math.max(2, Math.round(value)));
}

function readInitialRegionGridColumns(): number {
  if (typeof localStorage === 'undefined') return 3;
  const raw = localStorage.getItem(REGION_GRID_COLUMNS_STORAGE_KEY);
  if (raw === null) return 3;
  return normaliseRegionGridColumns(Number(raw));
}

function persistRegionGridColumns(value: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    REGION_GRID_COLUMNS_STORAGE_KEY,
    String(normaliseRegionGridColumns(value)),
  );
}

export function readInitialGpuEnabled(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(GPU_ENABLED_STORAGE_KEY) === 'true';
}

function persistGpuEnabled(value: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(GPU_ENABLED_STORAGE_KEY, String(value));
}

/**
 * A serialisable snapshot of per-region dither parameters. Mirrors the
 * dial-able fields from MethodParams but replaces embedded binary (pattern:
 * ImageData, stamp mask: ImageData) with string refIDs into the asset
 * registry. This makes RegionEntry JSON-safe and Swift Codable.
 *
 * All variants carry a `version` literal field (currently `1`) for forward
 * compatibility. When the snapshot shape changes in a future release, the
 * version discriminant enables clean v1 → v2 migration without ambiguity
 * (cf. Photoshop/Figma/VS Code schema-versioning precedent).
 */
export type MethodSnapshot =
  | MethodSnapshotA
  | MethodSnapshotB
  | MethodSnapshotC;

/**
 * Family A (halftone) snapshot. Not discriminated by algorithm - the
 * halftone renderer ignores `algorithmA`; it is carried in state only as
 * a placeholder satisfying the FamilyAMethod type union. The algorithm
 * picker applies exclusively to Family C (see state.ts:206).
 */
export type MethodSnapshotA = {
  version: 1;
  family: 'A';
  cellSize: number;
  stampType: StampType;
  stampCoverage: number;
  polarity: 'black' | 'white';
  density: number;
  blackThreshold: number;
  whiteThreshold: number;
  gridAngleDeg: number;
  shapeAngleDeg: number;
  shapeFlipX?: boolean;
  shapeFlipY?: boolean;
  algorithmJitter: number;
  patternNoiseScale?: number;
  /** Reference into the asset registry for a custom stamp mask (kind: 'stampMask'). */
  stampMaskRefId: StampMaskAssetId | null;
  patternRefId: null;
};

export type MethodSnapshotB = {
  version: 1;
  family: 'B';
  isGrayscale: boolean;
  blurRadius: number;
  patternBrightness: number;
  patternContrast: number;
  polarity: 'black' | 'white';
  density: number;
  blendMode: 'threshold' | 'multiply' | 'screen' | 'overlay' | 'softlight' | 'pinlight';
  patternScale: number;
  patternRotationDeg?: number;
  patternFlipX?: boolean;
  patternFlipY?: boolean;
  patternNoise: number;
  patternNoiseScale: number;
  patternHalftoneEnabled?: boolean;
  patternHalftoneCellSize?: number;
  patternHalftoneStampType?: StampType;
  patternHalftoneStampCoverage?: number;
  patternHalftonePolarity?: 'black' | 'white';
  patternHalftoneDensity?: number;
  patternHalftoneBlackThreshold?: number;
  patternHalftoneWhiteThreshold?: number;
  patternHalftoneGridAngleDeg?: number;
  patternHalftoneShapeAngleDeg?: number;
  patternHalftoneShapeFlipX?: boolean;
  patternHalftoneShapeFlipY?: boolean;
  patternHalftoneAlgorithmJitter?: number;
  patternHalftoneNoiseScale?: number;
  patternInvert: boolean;
  patternOpacity: number;
  blackThreshold: number;
  whiteThreshold: number;
  /** Reference into the asset registry for the uploaded pattern image (kind: 'pattern'). */
  patternRefId: PatternAssetId | null;
  stampMaskRefId: StampMaskAssetId | null;
};

type MethodSnapshotCCommon = {
  version: 1;
  family: 'C';
  cellSize: number;
  stampType: StampType;
  stampCoverage: number;
  polarity: 'black' | 'white';
  density: number;
  blackThreshold: number;
  whiteThreshold: number;
  gridAngleDeg: number;
  shapeAngleDeg: number;
  shapeFlipX?: boolean;
  shapeFlipY?: boolean;
  algorithmJitter: number;
  grainStrength?: number;
  grainScale?: number;
  /** Reference into the asset registry for a custom stamp mask (kind: 'stampMask'). */
  stampMaskRefId: StampMaskAssetId | null;
  patternRefId: null;
};

type MethodSnapshotCBayer = MethodSnapshotCCommon & { algorithm: 'bayer'; matrixSize: 2 | 4 | 8 | 16 };
type MethodSnapshotCBluenoise = MethodSnapshotCCommon & { algorithm: 'bluenoise' };
type MethodSnapshotCFloydSteinberg = MethodSnapshotCCommon & { algorithm: 'floyd-steinberg'; serpentine: boolean; strength: number };
type MethodSnapshotCAtkinson = MethodSnapshotCCommon & { algorithm: 'atkinson'; strength: number };
type MethodSnapshotCWerness = MethodSnapshotCCommon & { algorithm: 'werness'; brightness: number; contrast: number };

export type MethodSnapshotC =
  | MethodSnapshotCBayer
  | MethodSnapshotCBluenoise
  | MethodSnapshotCFloydSteinberg
  | MethodSnapshotCAtkinson
  | MethodSnapshotCWerness;

export type AssetKind = 'pattern' | 'stampMask';
export type PatternAssetId = `pattern:${string}`;
export type StampMaskAssetId = `stampMask:${string}`;
export type AssetId = PatternAssetId | StampMaskAssetId;

export type Asset = {
  /** Kind-scoped content-hash ID: `${kind}:${contentHash}`. */
  id: AssetId;
  kind: AssetKind;
  contentHash: string;
  width: number;
  height: number;
  uploadTs: number;
};

export type FamilyAAlgorithm =
  | 'bayer'
  | 'bluenoise'
  | 'floyd-steinberg'
  | 'atkinson'
  | 'werness';

export type BuiltinStampType =
  | 'pixel'
  | 'circle'
  | 'diamond'
  | 'line'
  | 'cross'
  | 'ellipse'
  | 'noise'
  | 'asterisk'
  | 'flower'
  | 'star'
  | 'heart'
  | 'spade'
  | 'club';
export type StampType = BuiltinStampType | 'custom';
export type PatternBlendMode = 'threshold' | 'multiply' | 'screen' | 'overlay' | 'softlight' | 'pinlight';

/**
 * Per-stamp default for algorithmJitter. The "pixel" stamp at coverage=1
 * fills the cell uniformly, so the algorithm needs the full pattern
 * range (jitter=1) to produce visible variation. Shaped stamps already
 * carry their own spatial profile and only need a small jitter (~0.075).
 * The core pipeline applies the same defaults when no algorithmJitter
 * is set; the demo mirrors them so the slider shows a sensible value
 * when the user picks a new stamp.
 */
export function defaultJitterForStamp(stamp: StampType): number {
  return stamp === 'pixel' ? 1 : 0.075;
}

/**
 * Per-region method override. `override === null` means the region renders
 * with whatever the global controls currently produce; otherwise the
 * frozen `MethodParams` snapshot is used. Re-snapshotting is done by
 * picking `Use default` and then re-picking the method in the regions
 * panel - there is no inline param editor in v0.2.0.
 */
export interface RegionConfig {
  /** 1..N. Label 0 is reserved for unlabelled / default-white pixels. */
  label: number;
  rgb: [number, number, number];
  override: MethodParams | null;
}

export type RemapPreset = 'clean' | 'balanced' | 'preserve' | 'custom';

export interface DemoState {
  // ===== v0.2.4 segmentation fields (additive; pipeline still reads regions[]) =====

  /**
   * One entry per palette colour in the loaded labelmap. Empty array when no
   * labelmap is loaded. Populated by buildLabelmapPatch; Item 3 will derive
   * `regions[]` from this instead of building it in parallel.
   */
  regionEntries: RegionEntry[];

  // ── Remap UI state (Item 22, plan A7.1) ────────────────────────────────────

  /**
   * Maximum cluster count (k) for the LAB k-means pre-parse pass.
   * Range 2..64. Default 32 ("Balanced" preset).
   */
  remapMaxK: number;

  /**
   * Post-merge delta-E threshold for the clustering pass.
   * Range 0..20. Default 4 ("Balanced" preset). 0 = no post-merge.
   */
  remapDeltaE: number;

  /**
   * Active preset selection. 'custom' = user has manually diverged from any
   * named preset by editing the sliders.
   */
  remapPreset: RemapPreset;

  /**
   * When true, the auto-preprocessing step on labelmap upload is disabled.
   * Only viable for already-flat labelmaps. Default false.
   */
  useRawLabelmap: boolean;

  /**
   * The raw (unprocessed) ImageData from the most recent labelmap upload.
   * Required for Remap to re-run preprocessing with different params on the
   * original source. null when no labelmap is loaded. Plan A3 internal remap.
   */
  originalLabelmapImage: ImageData | null;

  /**
   * Stack (region group) metadata for grouped region editing.
   */
  regionStacks: RegionStack[];

  /**
   * Asset metadata (JSON-safe). Binary pixel data lives in `assetData`.
   * Populated by the pattern / stamp-mask upload paths (Item 4).
   */
  assetLibrary: Asset[];

  /**
   * In-memory binary store for uploaded assets. Keyed by `Asset.id`.
   * NOT serialised. Declared as Map here to make the JSON boundary
   * explicit; callers never iterate this in the render loop (that is
   * Item 3's job).
   */
  assetData: Map<string, ImageData>;

  /**
   * LRU order for assetData. Least-recently-used first, most-recently-used
   * last. In-memory only and reset with each image.
   */
  assetDataAccessOrder: string[];

  /**
   * Compact label-plane: one byte per pixel, label index.
   * Length == source.width * source.height when a labelmap is loaded;
   * length == 0 otherwise.
   * Phase 0 memo v0.2.5.0-memory-desktop-memo.md §2: snapshot memory is
   * regionLabelPlane (1 B/px) plus labelMap indices (1 B/px) per snapshot.
   * Cap = 3 snapshots. TODO: re-evaluate after v0.2.5.0.
   */
  regionLabelPlane: Uint8Array;

  /**
   * UUIDs of currently selected region entries. Single-select in v0.2.4.0.
   * Multi-select is represented by multiple UUIDs in display order.
   */
  activeRegionUuids: string[];

  /**
   * UUID of the currently active stack - Item 32 (v0.2.4.3).
   *
   * When non-null, control-panel writes go to the stack's override rather
   * than individual region overrides. Mutually exclusive with a non-empty
   * activeRegionUuids: setting this non-null forces activeRegionUuids to [],
   * and setting activeRegionUuids non-empty forces this to null. The Store
   * enforces this invariant via Store.patch().
   *
   * Default: null.
   */
  activeStackUuid: string | null;

  /**
   * Minimum island size for the Fill Islands operation (Item 26).
   * Components with fewer pixels than this value are merged into their
   * largest 4-connected neighbour. Range 1..1000. Default 50.
   */
  fillIslandsMinPixels: number;

  /**
   * @deprecated Region highlighting is now drawn by the dedicated overlay
   * canvas from activeRegionUuids and uiActiveTab. This field is kept to avoid
   * breaking older undo snapshots; it is always false in new state.
   */
  showRegionOutline: boolean;

  /**
   * When true, clicking on the result canvas triggers a re-flood operation
   * (Item 27). The click coordinate is converted to label-plane space using
   * the current zoom/pan transform. Mode stays active until the user
   * clicks the toolbar toggle again (multiple-flood mode).
   */
  refloodMode: boolean;

  /**
   * Bounded undo stack for region operations (Item 28).
   * Each destructive op (Merge, Delete, Fill Islands, Reflood, Remap) pushes
   * a snapshot here before applying. Cmd/Ctrl+Z pops the top snapshot and
   * restores the region state. Capped at UNDO_STACK_MAX (3) entries.
   * In-memory only - not persisted, not part of preset serialisation.
   */
  regionUndoStack: RegionUndoSnapshot[];

  /**
   * Active sidebar tab - Item / Phase 3 UI state.
   * Drives which sidebar panel is rendered. Stored in DemoState (not localStorage)
   * so it survives undo and resets on new image.
   *
   * Default: 'tone'. Reset to 'tone' in createResetSegmentationPatch.
   */
  uiActiveTab: 'region' | 'tone' | 'template';
  templateStack: TemplateStackState;

  /**
   * Region thumbnail grid column count. Persisted locally because it is a
   * workspace UI preference, not image data. Range 2..5. Default 3.
   */
  uiRegionGridColumns: number;

  /**
   * Active-region overlay style. Render code (`drawRegionOverlay` in main.ts)
   * reads these values instead of hardcoded alpha and border width so future
   * UI can mutate them without touching render. The editor UI is intentionally
   * scoped to v0.2.5.0 per the master plan (`/Users/yuko/.claude/plans/
   * delegated-sleeping-dragon.md` "v0.2.5.0 carry-forward" - Yuko: "それは
   * 2.5.0 でいいか").
   */
  regionOverlay: RegionOverlayStyle;

  /**
   * Optional reference layer composited onto the dither output in the demo.
   * Image payload is intentionally in-memory only and is not persisted.
   */
  referenceOverlay: ReferenceOverlayState;

  /**
   * Persisted user preference for GPU acceleration. Default false for
   * v0.2.5.1 so the GPU path remains opt-in while the iPad matrix settles.
   */
  gpuEnabled: boolean;

  /**
   * Session-level GPU availability flag. B0 owns the lifecycle state machine;
   * B6 exposes the setting and diagnostic UI surface.
   */
  gpuEnabledForSession: boolean;

  /**
   * Per-frame backend diagnostics from the latest completed render. The status
   * footer uses this rather than the user preference so it reflects what
   * actually rendered the frame.
   */
  lastGpuDiagnostics: GpuRenderDiagnostics | null;

  /**
   * Active tone preset. 'custom' = the user has manually diverged from any
   * named preset by editing a field that a preset controls.
   * Set by the Tone preset dropdown; reverted to 'custom' by any manual edit
   * to a preset-controlled field.
   * Default: 'custom'.
   */
  tonePreset: string;

  /**
   * Tone preset application target.
   * 'global' preserves the existing behaviour. 'selection' applies named
   * presets to the active region or stack when one exists, otherwise it
   * falls back to global application. Transient UI state only.
   */
  tonePresetScope: 'global' | 'selection';

  /**
   * Source-adjust scope toggle (Item 11).
   * 'region' = apply brightness/contrast only to the active region's override.
   * 'global' = apply to global state regardless of active region.
   * Default: 'region'. When no region is active both modes behave the same.
   */
  sourceAdjustScope: 'region' | 'global';

  /**
   * Per-area source-adjust overrides (Item 11 - Phase A).
   * Keyed by region UUID (for active-region mode) or stack UUID (for
   * active-stack mode). When `sourceAdjustScope === 'region'` and a
   * region/stack is active, brightness/contrast writes land here rather
   * than on the global `sourceBrightness`/`sourceContrast` fields.
   *
   * The render path reads this map via buildAdjustedSource(), applying global
   * source adjust first and then compositing per-area overrides by label.
   */
  regionSourceAdjust: Record<string, { brightness: number; contrast: number }>;

  // ===== pre-existing fields (unchanged) =====

  /**
   * Adjusted grayscale source - what the dither pipeline reads.
   * Updated whenever sourceOriginal changes OR a source-adjust
   * slider changes.
   */
  source: ImageData | null;
  /**
   * Original loaded grayscale source. Immutable across source-adjust
   * slider changes. Used as the input to applySourceAdjust on every
   * brightness / contrast / gamma update so the adjustments are
   * non-destructive.
   */
  sourceOriginal: ImageData | null;
  /** -100..100. Applied via util/source-adjust before the dither pass. */
  sourceBrightness: number;
  /** -100..100. Applied via util/source-adjust before the dither pass. */
  sourceContrast: number;
  /** 0.1..3.0. Applied via util/source-adjust before the dither pass. */
  patternImage: ImageData | null;
  customStampMask: ImageData | null;
  /** Red-channel-indexed labelmap. null = single-region (default) path. */
  labelMap: ImageData | null;
  /** One entry per opaque non-black colour found in the labelmap, in scan order. Empty when labelMap === null. */
  regions: RegionConfig[];
  /** i18n key for the most recent labelmap-flow error. null when no error is showing. */
  labelmapError: string | null;
  /**
   * Family selector. v0.2.1 introduces Family C (stamp threshold) as
   * the byte-compat home of the v0.2.0 Family A renderer; in
   * subsequent commits Family A becomes the new halftone-dot
   * renderer, and the algorithm picker only applies to Family C.
   */
  family: 'A' | 'B' | 'C';
  algorithmA: FamilyAAlgorithm;
  cellSize: number;
  stampType: StampType;
  /**
   * Unified stamp coverage (0..1). Drives dot enclosing-square size for
   * every stamp shape: pixel side, circle diameter, diamond diagonal,
   * line thickness, custom mask footprint. v0.2.1 rename - replaces
   * the per-shape `stampThickness` (line) field. Line direction is
   * controlled by `shapeAngleDeg`; the v0.2.0 `stampAngle` field is
   * removed.
   */
  stampCoverage: number;
  polarity: 'black' | 'white';
  density: number;
  matrixSize: 2 | 4 | 8 | 16;
  serpentine: boolean;
  fsStrength: number;
  atkinsonStrength: number;
  brightness: number;
  contrast: number;
  isGrayscale: boolean;
  blurRadius: number;
  /**
   * Pattern image pre-dither adjustment - same shape as the source
   * adjust block in section 03 (brightness + contrast only). Replaces the
   * v0.2.3 levels black/white point pair.
   */
  patternBrightness: number;
  // Per-pixel halftone (Task D) + Family B sizing (E3) + shape rotation (E4).
  blackThreshold: number;
  whiteThreshold: number;
  gridAngleDeg: number;
  shapeAngleDeg: number;
  shapeFlipX: boolean;
  shapeFlipY: boolean;
  algorithmJitter: number;
  grainStrength: number;
  grainScale: number;
  patternBlendMode: PatternBlendMode;
  patternScale: number;
  patternRotationDeg: number;
  patternFlipX: boolean;
  patternFlipY: boolean;
  patternNoise: number;
  /**
   * 1..16. Sampling scale for the Custom Pattern blue-noise step
   * (both multiply and threshold branches share this knob).
   */
  patternNoiseScale: number;
  patternHalftoneEnabled: boolean;
  patternHalftoneCellSize: number;
  patternHalftoneStampType: StampType;
  patternHalftoneStampCoverage: number;
  patternHalftonePolarity: 'black' | 'white';
  patternHalftoneDensity: number;
  patternHalftoneBlackThreshold: number;
  patternHalftoneWhiteThreshold: number;
  patternHalftoneGridAngleDeg: number;
  patternHalftoneShapeAngleDeg: number;
  patternHalftoneShapeFlipX: boolean;
  patternHalftoneShapeFlipY: boolean;
  patternHalftoneAlgorithmJitter: number;
  patternHalftoneNoiseScale: number;
  patternInvert: boolean;
  patternContrast: number;
  patternOpacity: number;
}

/**
 * Generate a 4×4 Bayer ordered-dither matrix upscaled to 32×32 via
 * nearest-neighbour, returned as an RGBA ImageData.
 *
 * Bayer 4x4 - used as default Custom Pattern source so the canvas isn't
 * black before the user loads their own image (NEW3).
 *
 * Matrix:
 *   0  8  2 10
 *  12  4 14  6
 *   3 11  1  9
 *  15  7 13  5
 */
export function createDefaultPatternImage(): ImageData {
  const BAYER_4: ReadonlyArray<number> = [
     0,  8,  2, 10,
    12,  4, 14,  6,
     3, 11,  1,  9,
    15,  7, 13,  5,
  ];
  const TILE = 4;
  const SIZE = 32; // upscaled output dimensions
  const SCALE = SIZE / TILE; // = 8
  const data = new Uint8ClampedArray(SIZE * SIZE * 4);

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const ty = Math.floor(y / SCALE);
      const tx = Math.floor(x / SCALE);
      // Normalise Bayer value (0..15) to 0..255 grayscale.
      const v = Math.round((BAYER_4[ty * TILE + tx] ?? 0) * (255 / 15));
      const i = (y * SIZE + x) * 4;
      data[i] = v;       // R
      data[i + 1] = v;   // G
      data[i + 2] = v;   // B
      data[i + 3] = 255; // A
    }
  }
  return new ImageData(data, SIZE, SIZE);
}

export function createInitialState(): DemoState {
  return {
    // v0.2.4 segmentation fields
    regionEntries: [],
    regionStacks: [],
    assetLibrary: [],
    assetData: new Map(),
    assetDataAccessOrder: [],
    regionLabelPlane: new Uint8Array(0),
    activeRegionUuids: [],
    activeStackUuid: null,
    fillIslandsMinPixels: 4,
    showRegionOutline: false,
    refloodMode: false,
    regionUndoStack: [],
    // Phase 3 UI state - Tone tab is the default (Item 1).
    uiActiveTab: 'tone',
    templateStack: {
      activeTemplateId: null,
      name: null,
      groups: [],
      stacks: [],
      bindings: [],
    },
    uiRegionGridColumns: readInitialRegionGridColumns(),
    regionOverlay: { fillAlpha: 127, borderWidthPx: 2 },
    referenceOverlay: { image: null, opacity: 1, enabled: true },
    gpuEnabled: readInitialGpuEnabled(),
    gpuEnabledForSession: false,
    lastGpuDiagnostics: null,
    tonePreset: 'custom',
    tonePresetScope: 'global',
    sourceAdjustScope: 'region',
    regionSourceAdjust: {},
    // Remap UI state (Item 22)
    remapMaxK: 32,
    remapDeltaE: 4,
    remapPreset: 'balanced',
    useRawLabelmap: false,
    originalLabelmapImage: null,
    // pre-existing fields
    source: null,
    sourceOriginal: null,
    sourceBrightness: 0,
    sourceContrast: 0,
    patternImage: createDefaultPatternImage(),
    customStampMask: null,
    labelMap: null,
    regions: [],
    labelmapError: null,
    family: 'A',
    algorithmA: 'bayer',
    cellSize: 4,
    stampType: 'circle',
    stampCoverage: 0.9,
    polarity: 'black',
    density: 50,
    matrixSize: 8,
    serpentine: true,
    fsStrength: 1,
    atkinsonStrength: 1,
    brightness: 0,
    contrast: 0,
    isGrayscale: false,
    blurRadius: 0,
    patternBrightness: 0,
    blackThreshold: 0.02,
    whiteThreshold: 0.98,
    gridAngleDeg: 0,
    shapeAngleDeg: 0,
    shapeFlipX: false,
    shapeFlipY: false,
    algorithmJitter: 0.075,
    grainStrength: 0,
    grainScale: 4,
    patternBlendMode: 'threshold',
    patternScale: 1,
    patternRotationDeg: 0,
    patternFlipX: false,
    patternFlipY: false,
    patternNoise: 0,
    patternNoiseScale: 1,
    patternHalftoneEnabled: false,
    patternHalftoneCellSize: 8,
    patternHalftoneStampType: 'circle',
    patternHalftoneStampCoverage: 0.9,
    patternHalftonePolarity: 'black',
    patternHalftoneDensity: 50,
    patternHalftoneBlackThreshold: 0,
    patternHalftoneWhiteThreshold: 1,
    patternHalftoneGridAngleDeg: 0,
    patternHalftoneShapeAngleDeg: 0,
    patternHalftoneShapeFlipX: false,
    patternHalftoneShapeFlipY: false,
    patternHalftoneAlgorithmJitter: 0.075,
    patternHalftoneNoiseScale: 1,
    // v0.2.2 default: invert ON. White pattern background = white
    // output, black pattern shapes = black output. Matches WYSIWYG
    // expectations and Photoshop / Clip Studio Paint screentone
    // conventions. Toggle OFF to restore the v0.2.1 inverted semantic.
    patternInvert: true,
    patternContrast: 0,
    patternOpacity: 1,
  };
}

export const initialState: DemoState = createInitialState();

export type StatePatch = Partial<DemoState>;

/**
 * Build a patch that resets all per-image segmentation fields to their initial
 * values. Apply this in BOTH loadImage() and clearImage() to ensure stale
 * segmentation state never leaks across images.
 *
 * Defined here (not in main.ts) so the reset values share a single source of
 * truth with createInitialState(). Adding a new segmentation field requires
 * updating createInitialState() and this factory together.
 *
 * NOTE: `source` and `sourceOriginal` are intentionally excluded - loadImage
 * sets those to the new image data, and clearImage sets them to null. The
 * caller is responsible for providing those fields in the final patch.
 */
export function createResetSegmentationPatch(): StatePatch {
  const init = createInitialState();
  return {
    labelMap: null,
    regions: [],
    labelmapError: null,
    regionEntries: [],
    regionLabelPlane: init.regionLabelPlane,
    activeRegionUuids: [],
    activeStackUuid: null,
    refloodMode: false,
    originalLabelmapImage: null,
    regionUndoStack: [],
    regionStacks: [],
    // Phase 3 UI state - reset to Tone tab on new image (Item 1).
    uiActiveTab: 'tone',
    templateStack: init.templateStack,
    sourceAdjustScope: 'region',
    regionSourceAdjust: {},
    // Per-image fields that must reset when a new source is loaded.
    useRawLabelmap: init.useRawLabelmap,
    assetLibrary: init.assetLibrary,
    assetData: init.assetData,
    assetDataAccessOrder: init.assetDataAccessOrder,
    remapMaxK: init.remapMaxK,
    remapDeltaE: init.remapDeltaE,
    remapPreset: init.remapPreset,
    // NEW3: restore default Bayer pattern so a previously-loaded custom
    // pattern does not persist across a Clear/reload cycle.
    patternImage: init.patternImage,
  };
}
export type StateListener = (state: DemoState, patch: StatePatch) => void;

export class Store {
  private state: DemoState;
  private listeners: StateListener[] = [];

  constructor(initial: DemoState) {
    this.state = { ...initial };
  }

  get(): DemoState {
    return this.state;
  }

  patch(p: StatePatch): void {
    // Item 32: enforce active-selection mutual exclusion.
    // If the patch sets activeStackUuid non-null, clear activeRegionUuids.
    // If the patch sets activeRegionUuids non-empty, clear activeStackUuid.
    // Both fields may appear in the same patch - when the caller's patch is
    // internally contradictory (both non-null / non-empty), stack intent wins:
    // activeRegionUuids is cleared. A console.warn fires in dev.
    let normalised: StatePatch = p;

    const stackIsSet = p.activeStackUuid !== undefined && p.activeStackUuid !== null;
    const regionsIsSet = p.activeRegionUuids !== undefined && p.activeRegionUuids.length > 0;

    if (stackIsSet && regionsIsSet) {
      // Internally contradictory patch - stack intent wins.
      // DEV warning: gate on import.meta.env.DEV if available (Vite), else silent.
      // Cast through unknown to avoid strict ImportMeta type constraints.
      const metaEnv = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
      if (metaEnv?.DEV) {
        // biome-ignore lint/suspicious/noConsoleLog: dev-only warning
        console.warn(
          '[Store.patch] Contradictory patch: both activeStackUuid and activeRegionUuids are set. ' +
          'Stack intent wins - activeRegionUuids will be cleared.',
          p,
        );
      }
      normalised = { ...normalised, activeRegionUuids: [] };
    } else if (stackIsSet) {
      // Stack selected: clear region selection if not explicitly set in patch.
      if (p.activeRegionUuids === undefined) {
        normalised = { ...normalised, activeRegionUuids: [] };
      }
    } else if (regionsIsSet) {
      // Region(s) selected: clear stack selection if not explicitly set in patch.
      if (p.activeStackUuid === undefined) {
        normalised = { ...normalised, activeStackUuid: null };
      }
    }

    if (normalised.uiRegionGridColumns !== undefined) {
      const columns = normaliseRegionGridColumns(normalised.uiRegionGridColumns);
      normalised = { ...normalised, uiRegionGridColumns: columns };
      persistRegionGridColumns(columns);
    }

    if (normalised.gpuEnabled !== undefined) {
      persistGpuEnabled(normalised.gpuEnabled);
    }

    this.state = { ...this.state, ...normalised };
    for (const l of this.listeners) l(this.state, normalised);
  }

  subscribe(fn: StateListener): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((listener) => listener !== fn);
    };
  }
}

function stampFromState(state: DemoState): StampShape {
  if (state.stampType !== 'custom') {
    return { type: state.stampType, coverage: state.stampCoverage };
  }

  const mask = state.customStampMask;
  if (!mask) {
    // Fall back to a uniform pixel stamp if no custom mask uploaded.
    return { type: 'pixel', coverage: state.stampCoverage };
  }
  return { type: 'custom', coverage: state.stampCoverage, mask };
}

function familyAOrCFromState(
  state: DemoState,
  algorithm: FamilyAAlgorithm,
  family: 'A' | 'C',
): MethodParams {
  const common = {
    cellSize: state.cellSize,
    stamp: stampFromState(state),
    polarity: state.polarity,
    density: state.density,
    blackThreshold: state.blackThreshold,
    whiteThreshold: state.whiteThreshold,
    gridAngleDeg: state.gridAngleDeg,
    shapeAngleDeg: state.shapeAngleDeg,
    shapeFlipX: state.shapeFlipX,
    shapeFlipY: state.shapeFlipY,
    algorithmJitter: state.algorithmJitter,
  };
  const cGrain = family === 'C'
    ? { grainStrength: state.grainStrength, grainScale: state.grainScale }
    : {};
  // The two families share the same param shape during the v0.2.1
  // migration; only the discriminator differs. The `as never` cast is
  // local to this branch and lets TypeScript's exact-optional check
  // accept the unified literal type for both 'A' and 'C'.
  switch (algorithm) {
    case 'bayer': {
      if (family === 'A') {
        const method = {
          family: 'A' as const,
          ...common,
          patternNoiseScale: state.patternNoiseScale,
          algorithm: 'bayer' as const,
          matrixSize: state.matrixSize,
        };
        return method;
      }
      return { family: 'C', ...common, ...cGrain, algorithm: 'bayer', matrixSize: state.matrixSize };
    }
    case 'bluenoise': {
      if (family === 'A') {
        const method = {
          family: 'A' as const,
          ...common,
          patternNoiseScale: state.patternNoiseScale,
          algorithm: 'bluenoise' as const,
        };
        return method;
      }
      return { family: 'C', ...common, ...cGrain, algorithm: 'bluenoise' };
    }
    case 'floyd-steinberg': {
      if (family === 'A') {
        const method = {
          family: 'A' as const,
          ...common,
          patternNoiseScale: state.patternNoiseScale,
          algorithm: 'floyd-steinberg' as const,
          serpentine: state.serpentine,
          strength: state.fsStrength,
        };
        return method;
      }
      return {
        family: 'C',
        ...common,
        ...cGrain,
        algorithm: 'floyd-steinberg',
        serpentine: state.serpentine,
        strength: state.fsStrength,
      };
    }
    case 'atkinson': {
      if (family === 'A') {
        const method = {
          family: 'A' as const,
          ...common,
          patternNoiseScale: state.patternNoiseScale,
          algorithm: 'atkinson' as const,
          strength: state.atkinsonStrength,
        };
        return method;
      }
      return { family: 'C', ...common, ...cGrain, algorithm: 'atkinson', strength: state.atkinsonStrength };
    }
    case 'werness': {
      if (family === 'A') {
        const method = {
          family: 'A' as const,
          ...common,
          patternNoiseScale: state.patternNoiseScale,
          algorithm: 'werness' as const,
          brightness: state.brightness,
          contrast: state.contrast,
        };
        return method;
      }
      return {
        family: 'C',
        ...common,
        ...cGrain,
        algorithm: 'werness',
        brightness: state.brightness,
        contrast: state.contrast,
      };
    }
  }
}

function familyAFromState(state: DemoState, algorithm: FamilyAAlgorithm): MethodParams {
  return familyAOrCFromState(state, algorithm, 'A');
}

function familyCFromState(state: DemoState, algorithm: FamilyAAlgorithm): MethodParams {
  return familyAOrCFromState(state, algorithm, 'C');
}

function patternHalftoneStampFromState(state: DemoState): StampShape {
  if (state.patternHalftoneStampType === 'custom' && state.customStampMask) {
    return {
      type: 'custom',
      coverage: state.patternHalftoneStampCoverage,
      mask: state.customStampMask,
    };
  }
  if (state.patternHalftoneStampType === 'custom') {
    return { type: 'circle', coverage: state.patternHalftoneStampCoverage };
  }
  return {
    type: state.patternHalftoneStampType,
    coverage: state.patternHalftoneStampCoverage,
  };
}

function patternHalftoneValuesFromSnapshot(snapshot: MethodSnapshotB): {
  enabled: boolean;
  cellSize: number;
  stampType: StampType;
  stampCoverage: number;
  polarity: 'black' | 'white';
  density: number;
  blackThreshold: number;
  whiteThreshold: number;
  gridAngleDeg: number;
  shapeAngleDeg: number;
  shapeFlipX: boolean;
  shapeFlipY: boolean;
  algorithmJitter: number;
  noiseScale: number;
} {
  return {
    enabled: snapshot.patternHalftoneEnabled ?? false,
    cellSize: snapshot.patternHalftoneCellSize ?? 8,
    stampType: snapshot.patternHalftoneStampType ?? 'circle',
    stampCoverage: snapshot.patternHalftoneStampCoverage ?? 0.9,
    polarity: snapshot.patternHalftonePolarity ?? 'black',
    density: snapshot.patternHalftoneDensity ?? 50,
    blackThreshold: snapshot.patternHalftoneBlackThreshold ?? 0,
    whiteThreshold: snapshot.patternHalftoneWhiteThreshold ?? 1,
    gridAngleDeg: snapshot.patternHalftoneGridAngleDeg ?? 0,
    shapeAngleDeg: snapshot.patternHalftoneShapeAngleDeg ?? 0,
    shapeFlipX: snapshot.patternHalftoneShapeFlipX ?? false,
    shapeFlipY: snapshot.patternHalftoneShapeFlipY ?? false,
    algorithmJitter: snapshot.patternHalftoneAlgorithmJitter ?? 0.075,
    noiseScale: snapshot.patternNoiseScale ?? snapshot.patternHalftoneNoiseScale ?? 1,
  };
}

function familyBFromState(state: DemoState): MethodParams {
  // Pipeline contract requires a non-null pattern. The default Bayer tile keeps
  // Custom Pattern visible before the user uploads their own image.
  const pattern = state.patternImage ?? createDefaultPatternImage();
  return {
    family: 'B',
    algorithm: 'custom-pattern',
    pattern,
    isGrayscale: state.isGrayscale,
    blurRadius: state.blurRadius,
    patternBrightness: state.patternBrightness,
    patternContrast: state.patternContrast,
    polarity: state.polarity,
    density: state.density,
    blendMode: state.patternBlendMode,
    patternScale: state.patternScale,
    patternRotationDeg: state.patternRotationDeg,
    patternFlipX: state.patternFlipX,
    patternFlipY: state.patternFlipY,
    patternNoise: state.patternNoise,
    patternNoiseScale: state.patternNoiseScale,
    halftonePostprocess: {
      enabled: state.patternHalftoneEnabled,
      cellSize: state.patternHalftoneCellSize,
      stamp: patternHalftoneStampFromState(state),
      polarity: state.patternHalftonePolarity,
      density: state.patternHalftoneDensity,
      blackThreshold: state.patternHalftoneBlackThreshold,
      whiteThreshold: state.patternHalftoneWhiteThreshold,
      gridAngleDeg: state.patternHalftoneGridAngleDeg,
      shapeAngleDeg: state.patternHalftoneShapeAngleDeg,
      shapeFlipX: state.patternHalftoneShapeFlipX,
      shapeFlipY: state.patternHalftoneShapeFlipY,
      algorithmJitter: state.patternHalftoneAlgorithmJitter,
      patternNoiseScale: state.patternNoiseScale,
    },
    patternInvert: state.patternInvert,
    patternOpacity: state.patternOpacity,
    blackThreshold: state.blackThreshold,
    whiteThreshold: state.whiteThreshold,
  };
}

/**
 * Build a MethodParams object for the dither-core from current state.
 *
 * Family A is the halftone-dot renderer, Family C is the v0.2.0
 * stamp-threshold renderer, and Family B is the uploaded pattern renderer.
 */
export function buildMethod(state: DemoState): MethodParams {
  switch (state.family) {
    case 'A':
      return familyAFromState(state, state.algorithmA);
    case 'C':
      return familyCFromState(state, state.algorithmA);
    case 'B':
      return familyBFromState(state);
  }
}

/**
 * Options surfaced in the regions panel's per-region method dropdown.
 * Family A is a single "Halftone" entry (no algorithm picker). Family C
 * pins one of the five v0.2.0 algorithms. Family B is the single Custom
 * Pattern entry.
 */
export type OverrideOption =
  | { family: 'A' }
  | { family: 'B' }
  | { family: 'C'; algorithm: FamilyAAlgorithm };

/**
 * Snapshot the current global controls into a frozen `MethodParams`
 * for one region. The selected `OverrideOption` pins family (and
 * algorithm for Family C); every other parameter is read from state at
 * the moment of the call. To "edit" an override the user moves the
 * global controls and re-picks - there is no inline param editor.
 */
export function buildOverrideMethod(state: DemoState, option: OverrideOption): MethodParams {
  switch (option.family) {
    case 'A':
      // Halftone has no algorithm picker; algorithmA is read off
      // state only as a placeholder satisfying the FamilyAMethod
      // type union (the halftone renderer ignores it).
      return familyAFromState(state, state.algorithmA);
    case 'C':
      return familyCFromState(state, option.algorithm);
    case 'B':
      return familyBFromState(state);
  }
}

/**
 * Translate the demo state into the `regions` argument that
 * `dither({ source, labelMap, regions })` expects when running the
 * multi-region path.
 *
 * Uses `resolveRegionMethods(state)` as the canonical source so that
 * per-area patchMethod overrides and relink-resolved assets flow through
 * to the preview render. Warnings are discarded here - the controls layer
 * surfaces them via the status line (Item 15).
 *
 * If an entry's resolved override is null (no snapshot set, or snapshot
 * referenced a missing asset and resolveRegionMethods returned null),
 * the global `buildMethod(state)` fallback is used.
 *
 * Label 0 is intentionally never emitted - it is reserved for
 * unlabelled pixels and stays default-white per the dither-core
 * contract.
 */
export function buildRegions(state: DemoState): Region[] {
  const fallback = buildMethod(state);
  // Use resolveRegionMethods so per-area overrides + relinks flow through.
  // Discard warnings - the controls layer is responsible for surfacing them.
  const { regions } = resolveRegionMethods(state);
  return regions.map((region) => ({
    label: region.label,
    method: region.override ?? fallback,
  }));
}

/**
 * Scale a MethodParams' cellSize by a factor. Family A/C methods
 * get `cellSize *= scale` so the dot pitch stays constant in
 * source-space when the source is upscaled. Family B (custom
 * pattern) passes through unchanged -- the bigger source naturally
 * re-tiles the pattern more times at the same scale.
 */
function scaleMethodCellSize(method: MethodParams, scale: number): MethodParams {
  if (scale <= 1) return method;
  if (method.family === 'B') return method;
  return { ...method, cellSize: method.cellSize * scale };
}

/**
 * Build a MethodParams for a scaled export. Identical to
 * `buildMethod` when scale=1; for scale>1 the cellSize is
 * multiplied so the halftone dots stay the same source-space size
 * in the upscaled render.
 */
export function buildMethodAtScale(state: DemoState, scale: number): MethodParams {
  return scaleMethodCellSize(buildMethod(state), scale);
}

/**
 * Build regions for a scaled export. Same as `buildRegions` but
 * with cellSize scaled on every Family A/C method -- both the
 * global fallback and any per-region frozen overrides.
 *
 * Uses `resolveRegionMethods(state)` as the canonical source so that
 * per-area patchMethod overrides and relink-resolved assets flow through
 * to the export render, matching the preview render path.
 */
export function buildRegionsAtScale(state: DemoState, scale: number): Region[] {
  const fallback = buildMethodAtScale(state, scale);
  // Use resolveRegionMethods so per-area overrides + relinks flow through.
  // Discard warnings - the controls layer is responsible for surfacing them.
  const { regions } = resolveRegionMethods(state);
  return regions.map((region) => ({
    label: region.label,
    method: region.override
      ? scaleMethodCellSize(region.override, scale)
      : fallback,
  }));
}

// ---------------------------------------------------------------------------
// resolveRegionMethods - Item 3
// ---------------------------------------------------------------------------

/**
 * A structured warning emitted by resolveRegionMethods when a snapshot
 * references an asset that is not present in state.assetData. The ownerType
 * and ownerUuid identify whether the missing reference comes from a
 * region-level override or a stack-level override (load-bearing in v0.2.4.3).
 */
export type RegionWarning = {
  kind: 'missing-asset';
  ownerType: 'region' | 'stack';
  ownerUuid: string;
  assetKind: 'pattern' | 'stampMask';
  refId: string;
};

/**
 * Return type of resolveRegionMethods. `regions` is ready for the dither
 * pipeline; `warnings` carries structured missing-asset diagnostics for the
 * status line and badge layer (surfaced in Item 15).
 */
export type ResolvedRegions = {
  regions: RegionConfig[];
  warnings: RegionWarning[];
};

/**
 * Resolve a stamp shape from a snapshot's stampType / stampCoverage /
 * stampMaskRefId fields, looking up binary data in assetData.
 *
 * Returns the resolved StampShape and, if a custom mask was requested but
 * missing, a RegionWarning to emit.
 */
function resolveStamp(
  stampType: StampType,
  stampCoverage: number,
  stampMaskRefId: string | null,
  assetData: Map<string, ImageData>,
  ownerType: 'region' | 'stack',
  ownerUuid: string,
): { stamp: StampShape; warning: RegionWarning | null } {
  if (stampType !== 'custom') {
    return { stamp: { type: stampType, coverage: stampCoverage }, warning: null };
  }

  // Custom stamp - refId must be present and resolve to a loaded ImageData.
  if (stampMaskRefId !== null) {
    const mask = assetData.get(stampMaskRefId);
    if (mask !== undefined) {
      return { stamp: { type: 'custom', coverage: stampCoverage, mask }, warning: null };
    }
    // Plan A4.1: stamp mask missing -> fall back to pixel stamp (option a).
    const warning: RegionWarning = {
      kind: 'missing-asset',
      ownerType,
      ownerUuid,
      assetKind: 'stampMask',
      refId: stampMaskRefId,
    };
    return { stamp: { type: 'pixel', coverage: stampCoverage }, warning };
  }

  // stampType === 'custom' but no refId set: treat as a no-mask custom,
  // fall back to pixel stamp silently (no warning -- user hasn't linked one).
  return { stamp: { type: 'pixel', coverage: stampCoverage }, warning: null };
}

/**
 * Convert a MethodSnapshot to a pipeline-consumable MethodParams, resolving
 * asset refIDs through assetData and accumulating missing-asset warnings.
 */
function snapshotToMethodParams(
  snapshot: MethodSnapshot,
  assetData: Map<string, ImageData>,
  ownerType: 'region' | 'stack',
  ownerUuid: string,
  warnings: RegionWarning[],
): MethodParams {
  switch (snapshot.family) {
    case 'A': {
      const { stamp, warning } = resolveStamp(
        snapshot.stampType,
        snapshot.stampCoverage,
        snapshot.stampMaskRefId,
        assetData,
        ownerType,
        ownerUuid,
      );
      if (warning) warnings.push(warning);
      // Family A has a single halftone strategy; the algorithm field is
      // carried in snapshot only as a placeholder. Use 'bayer' as the
      // discriminant to satisfy the FamilyAMethod union.
      const method = {
        family: 'A' as const,
        algorithm: 'bayer' as const,
        matrixSize: 8 as const,
        cellSize: snapshot.cellSize,
        stamp,
        polarity: snapshot.polarity,
        density: snapshot.density,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
        gridAngleDeg: snapshot.gridAngleDeg,
        shapeAngleDeg: snapshot.shapeAngleDeg,
        shapeFlipX: snapshot.shapeFlipX ?? false,
        shapeFlipY: snapshot.shapeFlipY ?? false,
        algorithmJitter: snapshot.algorithmJitter,
        patternNoiseScale: snapshot.patternNoiseScale ?? 1,
      };
      return method;
    }

    case 'B': {
      // Resolve pattern image. Missing -> error placeholder + warning.
      let pattern: ImageData;
      if (snapshot.patternRefId !== null) {
        const found = assetData.get(snapshot.patternRefId);
        if (found !== undefined) {
          pattern = found;
        } else {
          const warning: RegionWarning = {
            kind: 'missing-asset',
            ownerType,
            ownerUuid,
            assetKind: 'pattern',
            refId: snapshot.patternRefId,
          };
          warnings.push(warning);
          pattern = createMissingPatternImage();
        }
      } else {
        // No ref set: use the same default Bayer pattern as the global path.
        pattern = createDefaultPatternImage();
      }
      const patternHalftone = patternHalftoneValuesFromSnapshot(snapshot);
      let patternHalftoneStamp: StampShape;
      let patternHalftoneWarning: RegionWarning | null = null;
      if (patternHalftone.stampType !== 'custom' || !patternHalftone.enabled) {
        patternHalftoneStamp = patternHalftone.stampType === 'custom'
          ? { type: 'circle', coverage: patternHalftone.stampCoverage }
          : { type: patternHalftone.stampType, coverage: patternHalftone.stampCoverage };
      } else if (snapshot.stampMaskRefId !== null) {
        const mask = assetData.get(snapshot.stampMaskRefId);
        if (mask !== undefined) {
          patternHalftoneStamp = {
            type: 'custom',
            coverage: patternHalftone.stampCoverage,
            mask,
          };
        } else {
          patternHalftoneWarning = {
            kind: 'missing-asset',
            ownerType,
            ownerUuid,
            assetKind: 'stampMask',
            refId: snapshot.stampMaskRefId,
          };
          patternHalftoneStamp = { type: 'circle', coverage: patternHalftone.stampCoverage };
        }
      } else {
        patternHalftoneStamp = { type: 'circle', coverage: patternHalftone.stampCoverage };
      }
      if (patternHalftoneWarning) warnings.push(patternHalftoneWarning);
      return {
        family: 'B',
        algorithm: 'custom-pattern',
        pattern,
        isGrayscale: snapshot.isGrayscale,
        blurRadius: snapshot.blurRadius,
        patternBrightness: snapshot.patternBrightness,
        patternContrast: snapshot.patternContrast,
        polarity: snapshot.polarity,
        density: snapshot.density,
        blendMode: snapshot.blendMode,
        patternScale: snapshot.patternScale,
        patternRotationDeg: snapshot.patternRotationDeg ?? 0,
        patternFlipX: snapshot.patternFlipX ?? false,
        patternFlipY: snapshot.patternFlipY ?? false,
        patternNoise: snapshot.patternNoise,
        patternNoiseScale: snapshot.patternNoiseScale,
        halftonePostprocess: {
          enabled: patternHalftone.enabled,
          cellSize: patternHalftone.cellSize,
          stamp: patternHalftoneStamp,
          polarity: patternHalftone.polarity,
          density: patternHalftone.density,
          blackThreshold: patternHalftone.blackThreshold,
          whiteThreshold: patternHalftone.whiteThreshold,
          gridAngleDeg: patternHalftone.gridAngleDeg,
          shapeAngleDeg: patternHalftone.shapeAngleDeg,
          shapeFlipX: patternHalftone.shapeFlipX,
          shapeFlipY: patternHalftone.shapeFlipY,
          algorithmJitter: patternHalftone.algorithmJitter,
          patternNoiseScale: patternHalftone.noiseScale,
        },
        patternInvert: snapshot.patternInvert,
        patternOpacity: snapshot.patternOpacity,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
      };
    }

    case 'C': {
      const { stamp, warning } = resolveStamp(
        snapshot.stampType,
        snapshot.stampCoverage,
        snapshot.stampMaskRefId,
        assetData,
        ownerType,
        ownerUuid,
      );
      if (warning) warnings.push(warning);
      const common = {
        family: 'C' as const,
        cellSize: snapshot.cellSize,
        stamp,
        polarity: snapshot.polarity,
        density: snapshot.density,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
        gridAngleDeg: snapshot.gridAngleDeg,
        shapeAngleDeg: snapshot.shapeAngleDeg,
        shapeFlipX: snapshot.shapeFlipX ?? false,
        shapeFlipY: snapshot.shapeFlipY ?? false,
        algorithmJitter: snapshot.algorithmJitter,
        grainStrength: snapshot.grainStrength ?? 0,
        grainScale: snapshot.grainScale ?? 4,
      };
      switch (snapshot.algorithm) {
        case 'bayer':
          return { ...common, algorithm: 'bayer', matrixSize: snapshot.matrixSize };
        case 'bluenoise':
          return { ...common, algorithm: 'bluenoise' };
        case 'floyd-steinberg':
          return { ...common, algorithm: 'floyd-steinberg', serpentine: snapshot.serpentine, strength: snapshot.strength };
        case 'atkinson':
          return { ...common, algorithm: 'atkinson', strength: snapshot.strength };
        case 'werness':
          return { ...common, algorithm: 'werness', brightness: snapshot.brightness, contrast: snapshot.contrast };
      }
    }
  }
}

/**
 * Derive the effective method family and algorithm for the current active
 * selection. Mirrors the priority order used by `syncVisibility` in
 * controls.ts:
 *
 *   1. Active stack (activeStackUuid non-null and stack found) → stack.override.
 *   2. Active region (activeRegionUuids[0] found and has override) → entry.override.
 *   3. Global state fallback.
 *
 * Returns `{ family, algorithmA }` where algorithmA is the algorithm
 * discriminant for Family C snapshots, or the global state.algorithmA for
 * all other cases. The returned pair is sufficient to derive a human-readable
 * method label.
 *
 * Pure function - no DOM, no side effects.
 */
export function effectiveMethodFamily(state: DemoState): {
  family: 'A' | 'B' | 'C';
  algorithmA: FamilyAAlgorithm;
} {
  // Priority 1 - active stack.
  if (state.activeStackUuid !== null) {
    const activeStack = state.regionStacks.find((s) => s.uuid === state.activeStackUuid);
    if (activeStack?.override !== undefined && activeStack.override !== null) {
      const snap = activeStack.override;
      return {
        family: snap.family,
        algorithmA: snap.family === 'C' ? snap.algorithm : state.algorithmA,
      };
    }
    // Stack exists but has no override → fall through to global.
  }

  // Priority 2 - active region (first selected entry).
  const activeUuid = state.activeRegionUuids[0];
  if (activeUuid !== undefined) {
    const activeEntry = state.regionEntries.find((e) => e.uuid === activeUuid);
    if (activeEntry?.override !== undefined && activeEntry.override !== null) {
      const snap = activeEntry.override;
      return {
        family: snap.family,
        algorithmA: snap.family === 'C' ? snap.algorithm : state.algorithmA,
      };
    }
  }

  // Priority 3 - global state fallback.
  return { family: state.family, algorithmA: state.algorithmA };
}

/**
 * The union of every dial value that the controls.ts sliders can read.
 * Every field is present regardless of the active family - fields that do not
 * belong to the active family's snapshot fall back to global state.
 *
 * Derived from the shared tone/method field set (ToneMethodField) so that
 * the field list stays in sync with MethodPartial and PRESET_CONTROLLED_KEY_LIST.
 */
export type EffectiveDialValues = Pick<DemoState, ToneMethodField>;

/**
 * Derive the effective dial values for the current active selection.
 * Priority: active stack override → active region override → global state.
 *
 * Returns a flat object containing every dial that controls.ts sliders read
 * from. When no override is in effect, returns the global state values.
 *
 * For fields that are not present in the active snapshot's family (e.g.
 * cellSize when the active override is Family B), the global state value is
 * used as a fallback. This matches the behaviour of controls that are hidden
 * when the wrong family is active - they will not display incorrect data.
 *
 * Pure function - no DOM, no side effects.
 */
export function effectiveDialValues(state: DemoState): EffectiveDialValues {
  // Resolve the active snapshot using the same priority logic as
  // effectiveMethodFamily. We repeat the lookup so this function is
  // self-contained and shares no mutable closure with the other helper.
  let snap: MethodSnapshot | null = null;

  // Priority 1 - active stack.
  if (state.activeStackUuid !== null) {
    const activeStack = state.regionStacks.find((s) => s.uuid === state.activeStackUuid);
    if (activeStack?.override != null) {
      snap = activeStack.override;
    }
  }

  // Priority 2 - active region (first selected entry), only if no stack snap.
  if (snap === null) {
    const activeUuid = state.activeRegionUuids[0];
    if (activeUuid !== undefined) {
      const activeEntry = state.regionEntries.find((e) => e.uuid === activeUuid);
      if (activeEntry?.override != null) {
        snap = activeEntry.override;
      }
    }
  }

  // Priority 3 - no override in effect: return all global values.
  if (snap === null) {
    return {
      family: state.family,
      algorithmA: state.algorithmA,
      cellSize: state.cellSize,
      stampType: state.stampType,
      stampCoverage: state.stampCoverage,
      polarity: state.polarity,
      density: state.density,
      blackThreshold: state.blackThreshold,
      whiteThreshold: state.whiteThreshold,
      gridAngleDeg: state.gridAngleDeg,
      shapeAngleDeg: state.shapeAngleDeg,
      shapeFlipX: state.shapeFlipX,
      shapeFlipY: state.shapeFlipY,
      algorithmJitter: state.algorithmJitter,
      grainStrength: state.grainStrength,
      grainScale: state.grainScale,
      matrixSize: state.matrixSize,
      serpentine: state.serpentine,
      fsStrength: state.fsStrength,
      atkinsonStrength: state.atkinsonStrength,
      brightness: state.brightness,
      contrast: state.contrast,
      isGrayscale: state.isGrayscale,
      blurRadius: state.blurRadius,
      patternBrightness: state.patternBrightness,
      patternContrast: state.patternContrast,
      patternBlendMode: state.patternBlendMode,
      patternScale: state.patternScale,
      patternRotationDeg: state.patternRotationDeg,
      patternFlipX: state.patternFlipX,
      patternFlipY: state.patternFlipY,
      patternNoise: state.patternNoise,
      patternNoiseScale: state.patternNoiseScale,
      patternHalftoneEnabled: state.patternHalftoneEnabled,
      patternHalftoneCellSize: state.patternHalftoneCellSize,
      patternHalftoneStampType: state.patternHalftoneStampType,
      patternHalftoneStampCoverage: state.patternHalftoneStampCoverage,
      patternHalftonePolarity: state.patternHalftonePolarity,
      patternHalftoneDensity: state.patternHalftoneDensity,
      patternHalftoneBlackThreshold: state.patternHalftoneBlackThreshold,
      patternHalftoneWhiteThreshold: state.patternHalftoneWhiteThreshold,
      patternHalftoneGridAngleDeg: state.patternHalftoneGridAngleDeg,
      patternHalftoneShapeAngleDeg: state.patternHalftoneShapeAngleDeg,
      patternHalftoneShapeFlipX: state.patternHalftoneShapeFlipX,
      patternHalftoneShapeFlipY: state.patternHalftoneShapeFlipY,
      patternHalftoneAlgorithmJitter: state.patternHalftoneAlgorithmJitter,
      patternHalftoneNoiseScale: state.patternNoiseScale,
      patternInvert: state.patternInvert,
      patternOpacity: state.patternOpacity,
    };
  }

  // Derive family and algorithmA from the snapshot.
  const family = snap.family;
  const algorithmA: FamilyAAlgorithm =
    family === 'C' ? snap.algorithm : state.algorithmA;

  // Read each field from the snapshot when available, falling back to global
  // state for fields that do not exist in the snapshot's family.
  //
  // Family A / C share most fields (cellSize, stampType, etc.).
  // Family B has a completely different shape (patternBrightness, blurRadius, etc.).
  // Fields from the wrong family fall back to global state - those controls
  // are hidden when that family is not active, so the value is not visible.

  if (snap.family === 'A') {
    return {
      family: 'A',
      algorithmA,
      cellSize: snap.cellSize,
      stampType: snap.stampType,
      stampCoverage: snap.stampCoverage,
      polarity: snap.polarity,
      density: snap.density,
      blackThreshold: snap.blackThreshold,
      whiteThreshold: snap.whiteThreshold,
      gridAngleDeg: snap.gridAngleDeg,
      shapeAngleDeg: snap.shapeAngleDeg,
      shapeFlipX: snap.shapeFlipX ?? false,
      shapeFlipY: snap.shapeFlipY ?? false,
      algorithmJitter: snap.algorithmJitter,
      grainStrength: state.grainStrength,
      grainScale: state.grainScale,
      // Family C-specific - not in Family A snapshot, fall back to global.
      matrixSize: state.matrixSize,
      serpentine: state.serpentine,
      fsStrength: state.fsStrength,
      atkinsonStrength: state.atkinsonStrength,
      brightness: state.brightness,
      contrast: state.contrast,
      // Family B-specific - patternNoiseScale is shared by Halftone noise size.
      isGrayscale: state.isGrayscale,
      blurRadius: state.blurRadius,
      patternBrightness: state.patternBrightness,
      patternContrast: state.patternContrast,
      patternBlendMode: state.patternBlendMode,
      patternScale: state.patternScale,
      patternRotationDeg: state.patternRotationDeg,
      patternFlipX: state.patternFlipX,
      patternFlipY: state.patternFlipY,
      patternNoise: state.patternNoise,
      patternNoiseScale: snap.patternNoiseScale ?? state.patternNoiseScale,
      patternHalftoneEnabled: state.patternHalftoneEnabled,
      patternHalftoneCellSize: state.patternHalftoneCellSize,
      patternHalftoneStampType: state.patternHalftoneStampType,
      patternHalftoneStampCoverage: state.patternHalftoneStampCoverage,
      patternHalftonePolarity: state.patternHalftonePolarity,
      patternHalftoneDensity: state.patternHalftoneDensity,
      patternHalftoneBlackThreshold: state.patternHalftoneBlackThreshold,
      patternHalftoneWhiteThreshold: state.patternHalftoneWhiteThreshold,
      patternHalftoneGridAngleDeg: state.patternHalftoneGridAngleDeg,
      patternHalftoneShapeAngleDeg: state.patternHalftoneShapeAngleDeg,
      patternHalftoneShapeFlipX: state.patternHalftoneShapeFlipX,
      patternHalftoneShapeFlipY: state.patternHalftoneShapeFlipY,
      patternHalftoneAlgorithmJitter: state.patternHalftoneAlgorithmJitter,
      patternHalftoneNoiseScale: snap.patternNoiseScale ?? state.patternNoiseScale,
      patternInvert: state.patternInvert,
      patternOpacity: state.patternOpacity,
    };
  }

  if (snap.family === 'B') {
    const patternHalftone = patternHalftoneValuesFromSnapshot(snap);
    return {
      family: 'B',
      algorithmA,
      // Family A/C-specific - not in Family B snapshot, fall back to global.
      cellSize: state.cellSize,
      stampType: state.stampType,
      stampCoverage: state.stampCoverage,
      gridAngleDeg: state.gridAngleDeg,
      shapeAngleDeg: state.shapeAngleDeg,
      shapeFlipX: state.shapeFlipX,
      shapeFlipY: state.shapeFlipY,
      algorithmJitter: state.algorithmJitter,
      grainStrength: state.grainStrength,
      grainScale: state.grainScale,
      matrixSize: state.matrixSize,
      serpentine: state.serpentine,
      fsStrength: state.fsStrength,
      atkinsonStrength: state.atkinsonStrength,
      brightness: state.brightness,
      contrast: state.contrast,
      // Shared fields (polarity, density, thresholds exist in all families).
      polarity: snap.polarity,
      density: snap.density,
      blackThreshold: snap.blackThreshold,
      whiteThreshold: snap.whiteThreshold,
      // Family B-specific fields.
      isGrayscale: snap.isGrayscale,
      blurRadius: snap.blurRadius,
      patternBrightness: snap.patternBrightness,
      patternContrast: snap.patternContrast,
      patternBlendMode: snap.blendMode,
      patternScale: snap.patternScale,
      patternRotationDeg: snap.patternRotationDeg ?? 0,
      patternFlipX: snap.patternFlipX ?? false,
      patternFlipY: snap.patternFlipY ?? false,
      patternNoise: snap.patternNoise,
      patternNoiseScale: snap.patternNoiseScale,
      patternHalftoneEnabled: patternHalftone.enabled,
      patternHalftoneCellSize: patternHalftone.cellSize,
      patternHalftoneStampType: patternHalftone.stampType,
      patternHalftoneStampCoverage: patternHalftone.stampCoverage,
      patternHalftonePolarity: patternHalftone.polarity,
      patternHalftoneDensity: patternHalftone.density,
      patternHalftoneBlackThreshold: patternHalftone.blackThreshold,
      patternHalftoneWhiteThreshold: patternHalftone.whiteThreshold,
      patternHalftoneGridAngleDeg: patternHalftone.gridAngleDeg,
      patternHalftoneShapeAngleDeg: patternHalftone.shapeAngleDeg,
      patternHalftoneShapeFlipX: patternHalftone.shapeFlipX,
      patternHalftoneShapeFlipY: patternHalftone.shapeFlipY,
      patternHalftoneAlgorithmJitter: patternHalftone.algorithmJitter,
      patternHalftoneNoiseScale: patternHalftone.noiseScale,
      patternInvert: snap.patternInvert,
      patternOpacity: snap.patternOpacity,
    };
  }

  // Family C - read common C fields, then algorithm-specific.
  // All Family C algorithm variants share cellSize, stampType, etc. via
  // MethodSnapshotCCommon. Algorithm-specific fields (matrixSize,
  // serpentine, strength, brightness/contrast) depend on the algorithm.
  const cCommon = {
    family: 'C' as const,
    algorithmA,
    cellSize: snap.cellSize,
    stampType: snap.stampType,
    stampCoverage: snap.stampCoverage,
    polarity: snap.polarity,
    density: snap.density,
    blackThreshold: snap.blackThreshold,
    whiteThreshold: snap.whiteThreshold,
    gridAngleDeg: snap.gridAngleDeg,
    shapeAngleDeg: snap.shapeAngleDeg,
    shapeFlipX: snap.shapeFlipX ?? false,
    shapeFlipY: snap.shapeFlipY ?? false,
    algorithmJitter: snap.algorithmJitter,
    grainStrength: snap.grainStrength ?? state.grainStrength,
    grainScale: snap.grainScale ?? state.grainScale,
    // Family B-specific - fall back to global.
    isGrayscale: state.isGrayscale,
    blurRadius: state.blurRadius,
    patternBrightness: state.patternBrightness,
    patternContrast: state.patternContrast,
    patternBlendMode: state.patternBlendMode,
    patternScale: state.patternScale,
    patternRotationDeg: state.patternRotationDeg,
    patternFlipX: state.patternFlipX,
    patternFlipY: state.patternFlipY,
    patternNoise: state.patternNoise,
    patternNoiseScale: state.patternNoiseScale,
    patternHalftoneEnabled: state.patternHalftoneEnabled,
    patternHalftoneCellSize: state.patternHalftoneCellSize,
    patternHalftoneStampType: state.patternHalftoneStampType,
    patternHalftoneStampCoverage: state.patternHalftoneStampCoverage,
    patternHalftonePolarity: state.patternHalftonePolarity,
    patternHalftoneDensity: state.patternHalftoneDensity,
    patternHalftoneBlackThreshold: state.patternHalftoneBlackThreshold,
    patternHalftoneWhiteThreshold: state.patternHalftoneWhiteThreshold,
    patternHalftoneGridAngleDeg: state.patternHalftoneGridAngleDeg,
    patternHalftoneShapeAngleDeg: state.patternHalftoneShapeAngleDeg,
    patternHalftoneShapeFlipX: state.patternHalftoneShapeFlipX,
    patternHalftoneShapeFlipY: state.patternHalftoneShapeFlipY,
    patternHalftoneAlgorithmJitter: state.patternHalftoneAlgorithmJitter,
    patternHalftoneNoiseScale: state.patternNoiseScale,
    patternInvert: state.patternInvert,
    patternOpacity: state.patternOpacity,
  };

  switch (snap.algorithm) {
    case 'bayer':
      return {
        ...cCommon,
        matrixSize: snap.matrixSize,
        serpentine: state.serpentine,
        fsStrength: state.fsStrength,
        atkinsonStrength: state.atkinsonStrength,
        brightness: state.brightness,
        contrast: state.contrast,
      };
    case 'bluenoise':
      return {
        ...cCommon,
        matrixSize: state.matrixSize,
        serpentine: state.serpentine,
        fsStrength: state.fsStrength,
        atkinsonStrength: state.atkinsonStrength,
        brightness: state.brightness,
        contrast: state.contrast,
      };
    case 'floyd-steinberg':
      return {
        ...cCommon,
        matrixSize: state.matrixSize,
        serpentine: snap.serpentine,
        fsStrength: snap.strength,
        atkinsonStrength: state.atkinsonStrength,
        brightness: state.brightness,
        contrast: state.contrast,
      };
    case 'atkinson':
      return {
        ...cCommon,
        matrixSize: state.matrixSize,
        serpentine: state.serpentine,
        fsStrength: state.fsStrength,
        atkinsonStrength: snap.strength,
        brightness: state.brightness,
        contrast: state.contrast,
      };
    case 'werness':
      return {
        ...cCommon,
        matrixSize: state.matrixSize,
        serpentine: state.serpentine,
        fsStrength: state.fsStrength,
        atkinsonStrength: state.atkinsonStrength,
        brightness: snap.brightness,
        contrast: snap.contrast,
      };
  }
}

/**
 * Derive pipeline-consumable RegionConfig[] from the canonical regionEntries
 * array, resolving per-entry and per-stack MethodSnapshots against assetData.
 *
 * Principle A4 (pure function): no DOM, no console.log, no side effects.
 * The caller (main.ts render loop, Item 15 warnings UI) decides what to do
 * with the returned warnings.
 *
 * Override composition (plan A1):
 *   effective = entry.override ?? stack.override ?? null
 *
 * If effective is null, the resulting RegionConfig.override is also null,
 * meaning the region renders with the global method. No warning is emitted
 * for a null effective snapshot.
 */
export function resolveRegionMethods(state: DemoState): ResolvedRegions {
  const warnings: RegionWarning[] = [];
  const stackMap = new Map<string, RegionStack>(
    state.regionStacks.map((s) => [s.uuid, s]),
  );

  const regions: RegionConfig[] = state.regionEntries.map((entry) => {
    // Compose effective snapshot: entry-level wins, then stack-level, then null.
    const stack = entry.stackId !== null ? stackMap.get(entry.stackId) : undefined;
    const effectiveSnapshot = entry.override ?? stack?.override ?? null;

    let override: MethodParams | null = null;
    if (effectiveSnapshot !== null) {
      // Determine owner attribution for warnings: if entry has its own
      // override use 'region'; otherwise the effective snapshot came from
      // the stack.
      const ownerType: 'region' | 'stack' =
        entry.override !== null ? 'region' : 'stack';
      const ownerUuid = ownerType === 'region' ? entry.uuid : (entry.stackId ?? entry.uuid);
      override = snapshotToMethodParams(
        effectiveSnapshot,
        state.assetData,
        ownerType,
        ownerUuid,
        warnings,
      );
    }

    return {
      label: entry.label,
      rgb: entry.rgb,
      override,
    };
  });

  return { regions, warnings };
}
